import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
      
      // Calculate unread count
      const unread = res.data.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Connect to SSE stream
  const connectToStream = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create EventSource with auth header via fetch
    const streamUrl = `${API}/notifications/stream`;
    
    const fetchStream = async () => {
      try {
        const response = await fetch(streamUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream'
          }
        });

        if (!response.ok) {
          throw new Error('Stream connection failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        setIsConnected(true);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const text = decoder.decode(value);
          const lines = text.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                handleStreamEvent(data);
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        }
      } catch (error) {
        console.error('SSE connection error:', error);
        setIsConnected(false);
        
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectToStream();
        }, 5000);
      }
    };

    fetchStream();
  }, []);

  // Handle incoming stream events
  const handleStreamEvent = useCallback((event) => {
    switch (event.type) {
      case 'connected':
        console.log('Notifications stream connected');
        break;
        
      case 'notification':
        // Add new notification to list
        setNotifications(prev => [event.data, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Show toast notification
        showNotificationToast(event.data);
        
        // Play sound
        playNotificationSound();
        break;
        
      case 'heartbeat':
        setUnreadCount(event.unread_count);
        break;
        
      case 'error':
        console.error('Stream error:', event.message);
        break;
        
      default:
        break;
    }
  }, []);

  // Show toast notification
  const showNotificationToast = (notification) => {
    const icons = {
      'step_advanced': '🚀',
      'document_uploaded': '📄',
      'document_approved': '✅',
      'case_created': '🎉',
      'system': '🔔'
    };
    
    const icon = icons[notification.type] || '🔔';
    
    toast(
      <div className="flex items-start gap-3">
        <span className="text-xl">{icon}</span>
        <div>
          <p className="font-medium text-navy-900">{notification.message}</p>
          <p className="text-xs text-navy-500 mt-1">Justo ahora</p>
        </div>
      </div>,
      {
        duration: 5000,
        position: 'top-right'
      }
    );
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      // Audio not supported
    }
  };

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    fetchNotifications();
    connectToStream();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [fetchNotifications, connectToStream]);

  return {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
};

export default useNotifications;
